import { useState, useEffect } from 'react';
import PropertiesManagement from './PropertiesManagement';
import UserManagement from './UserManagement';
import PasswordSettings from './PasswordSettings';

interface DashboardStats {
  totalProperties: number;
  activeProperties: number;
  featuredProperties: number;
  totalCities: number;
  totalUsers: number;
  totalViews: number;
  totalInquiries: number;
  totalAppointments: number;
  conversionRate: number;
}

interface DashboardProps {
  onSectionChange?: (section: string) => void;
}

export default function Dashboard({ onSectionChange }: DashboardProps) {
  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    activeProperties: 0,
    featuredProperties: 0,
    totalCities: 0,
    totalUsers: 0,
    totalViews: 0,
    totalInquiries: 0,
    totalAppointments: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [viewsHistory, setViewsHistory] = useState<{ date: string; count: number }[]>([]);
  const [inquiriesHistory, setInquiriesHistory] = useState<{ date: string; count: number }[]>([]);

  useEffect(() => {
    fetchStats();
    initializeAnalytics();
  }, []);

  const fetchStats = async () => {
    try {
      // Initialize with zeros first
      setStats({
        totalProperties: 0,
        activeProperties: 0,
        featuredProperties: 0,
        totalCities: 0,
        totalUsers: 0,
        totalViews: 0,
        totalInquiries: 0,
        totalAppointments: 0,
        conversionRate: 0
      });

      const [propertiesRes, citiesRes, usersRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ action: 'all' })
        }),
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ action: 'cities' })
        }),
        fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ action: 'get_users' })
        })
      ]);

      if (propertiesRes.ok) {
        const propertiesData = await propertiesRes.json();
        const properties = propertiesData.properties || [];
        
        setStats(prev => ({
          ...prev,
          totalProperties: properties.length,
          activeProperties: properties.filter((p: any) => p.is_active).length,
          featuredProperties: properties.filter((p: any) => p.is_featured).length
        }));
      }

      if (citiesRes.ok) {
        const citiesData = await citiesRes.json();
        setStats(prev => ({
          ...prev,
          totalCities: citiesData.cities?.length || 0
        }));
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setStats(prev => ({
          ...prev,
          totalUsers: usersData.users?.length || 1 // At least admin user
        }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Keep stats at zero if there's an error
    } finally {
      setLoading(false);
    }
  };

  const initializeAnalytics = () => {
    try {
      // Get or initialize analytics from localStorage
      const savedAnalytics = localStorage.getItem('property_analytics');
      let analyticsData = {
        totalViews: 0,
        totalInquiries: 0,
        totalAppointments: 0,
        dailyViews: {},
        dailyInquiries: {}
      };

      if (savedAnalytics) {
        analyticsData = JSON.parse(savedAnalytics);
      } else {
        // Initialize with some sample data for demonstration
        analyticsData = {
          totalViews: 0,
          totalInquiries: 0,
          totalAppointments: 0,
          dailyViews: {},
          dailyInquiries: {}
        };
        localStorage.setItem('property_analytics', JSON.stringify(analyticsData));
      }

      // Update stats with analytics data
      setStats(prev => ({
        ...prev,
        totalViews: analyticsData.totalViews,
        totalInquiries: analyticsData.totalInquiries,
        totalAppointments: analyticsData.totalAppointments,
        conversionRate: analyticsData.totalViews > 0 ? (analyticsData.totalInquiries / analyticsData.totalViews) * 100 : 0
      }));

      // Generate history data for the last 7 days
      const generateHistory = (dailyData: Record<string, number>) => {
        const history = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          history.push({
            date: dateStr,
            count: dailyData[dateStr] || 0
          });
        }
        return history;
      };

      setViewsHistory(generateHistory(analyticsData.dailyViews));
      setInquiriesHistory(generateHistory(analyticsData.dailyInquiries));

    } catch (error) {
      console.error('Error initializing analytics:', error);
      // Initialize empty history if there's an error
      const emptyHistory = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        emptyHistory.push({
          date: date.toISOString().split('T')[0],
          count: 0
        });
      }
      setViewsHistory(emptyHistory);
      setInquiriesHistory(emptyHistory);
    }
  };

  const maxViews = Math.max(...viewsHistory.map(d => d.count), 1);
  const maxInquiries = Math.max(...inquiriesHistory.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard General</h2>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="w-8 h-8 animate-spin">
              <i className="ri-loader-4-line text-2xl text-blue-600"></i>
            </div>
          </div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-building-line text-blue-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Propiedades</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalProperties}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Propiedades Activas</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.activeProperties}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-star-line text-yellow-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Destacadas</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.featuredProperties}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-team-line text-purple-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Usuarios</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <i className="ri-eye-line text-indigo-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Visualizaciones</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalViews.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="ri-message-line text-emerald-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Consultas</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalInquiries}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-orange-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Citas</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalAppointments}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                    <i className="ri-percent-line text-rose-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Tasa Conversión</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.conversionRate.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Views Chart */}
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Visualizaciones (Últimos 7 días)</h3>
                <div className="h-40 flex items-end space-x-2">
                  {viewsHistory.map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-blue-500 rounded-t"
                        style={{
                          height: `${(day.count / maxViews) * 100}%`,
                          minHeight: day.count > 0 ? '8px' : '2px'
                        }}
                        title={`${day.date}: ${day.count} visualizaciones`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-2">
                        {new Date(day.date).getDate()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-2 text-sm text-gray-500">
                  Total: {viewsHistory.reduce((sum, day) => sum + day.count, 0)} visualizaciones
                </div>
              </div>

              {/* Inquiries Chart */}
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Consultas (Últimos 7 días)</h3>
                <div className="h-40 flex items-end space-x-2">
                  {inquiriesHistory.map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full bg-green-500 rounded-t"
                        style={{
                          height: `${(day.count / maxInquiries) * 100}%`,
                          minHeight: day.count > 0 ? '8px' : '2px'
                        }}
                        title={`${day.date}: ${day.count} consultas`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-2">
                        {new Date(day.date).getDate()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-2 text-sm text-gray-500">
                  Total: {inquiriesHistory.reduce((sum, day) => sum + day.count, 0)} consultas
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestión Principal</h3>
          <div className="space-y-3">
            <button
              onClick={() => onSectionChange?.('properties')}
              className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center">
                <i className="ri-building-line text-blue-600 mr-3"></i>
                <span className="font-medium">Gestionar Propiedades</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400"></i>
            </button>
            
            <button
              onClick={() => onSectionChange?.('users')}
              className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center">
                <i className="ri-team-line text-blue-600 mr-3"></i>
                <span className="font-medium">Gestionar Usuarios</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400"></i>
            </button>

            <button
              onClick={() => onSectionChange?.('leads')}
              className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center">
                <i className="ri-customer-service-line text-green-600 mr-3"></i>
                <span className="font-medium">Gestión de Leads</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400"></i>
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuración</h3>
          <div className="space-y-3">
            <button
              onClick={() => onSectionChange?.('password')}
              className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center">
                <i className="ri-lock-password-line text-blue-600 mr-3"></i>
                <span className="font-medium">Seguridad y Contraseñas</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400"></i>
            </button>
            
            <button
              onClick={() => onSectionChange?.('settings')}
              className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center">
                <i className="ri-settings-3-line text-blue-600 mr-3"></i>
                <span className="font-medium">Configuración del Sitio</span>
              </div>
              <i className="ri-arrow-right-line text-gray-400"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Insights de Rendimiento</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 mb-1">
              {stats.totalViews > 0 ? ((stats.totalInquiries / stats.totalViews) * 100).toFixed(1) : '0.0'}%
            </div>
            <p className="text-sm text-gray-600">Tasa de Conversión a Consultas</p>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">
              {Math.round(stats.totalViews / 7)}
            </div>
            <p className="text-sm text-gray-600">Promedio Visualizaciones/Día</p>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 mb-1">
              {stats.totalInquiries > 0 ? ((stats.totalAppointments / stats.totalInquiries) * 100).toFixed(1) : '0.0'}%
            </div>
            <p className="text-sm text-gray-600">Tasa de Conversión a Citas</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="space-y-3">
          <div className="flex items-center text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
            <span className="text-gray-600">Sistema de subida de archivos habilitado</span>
            <span className="ml-auto text-xs text-gray-400">Hoy</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span className="text-gray-600">Integración con Google Maps configurada</span>
            <span className="ml-auto text-xs text-gray-400">Hoy</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
            <span className="text-gray-600">Análisis estadístico por propiedad disponible</span>
            <span className="ml-auto text-xs text-gray-400">Hoy</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
            <span className="text-gray-600">Navegación del panel mejorada</span>
            <span className="ml-auto text-xs text-gray-400">Hoy</span>
          </div>
        </div>
      </div>
    </div>
  );
}
